"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.init = void 0;
const jwt_decode_1 = __importDefault(require("jwt-decode"));
const uuid_1 = require("uuid");
const constant_1 = require("./constant");
const signOutService_1 = require("./service/signOutService");
const tokenService_1 = require("./service/tokenService");
const storage_1 = require("./storage");
/**
 * This function initializes the auth module and returns the auth object with all the methods.
 * During initialization, check if there is an access token in cookie,
 * if the access token is valid, do nothing, if the access token is invalid,
 * check if there is a refresh token and session id, if yes, get new access token.
 * ```
 * const auth = await init({
 *      clientId: "clientId",
 *      clientSecret: "clientSecret",
 *      redirectUri: "redirectUri",
 *      deviceId: "deviceId",
 *      authDomain: "authDomain",
 *      scope: "scope",
 *  });
 * auth.signInWithRedirect();
 * ```
 *
 */
const init = async (authParams) => {
    const authPayload = authParams;
    const accessToken = (0, storage_1.getItem)("token");
    const refreshToken = (0, storage_1.getItem)("refreshToken");
    const sessionId = (0, storage_1.getItem)("sessionId");
    const anonymousId = (0, storage_1.getItem)("ajs_anonymous_id");
    const cookieDomain = authPayload.cookieDomain;
    // Check is access token is still valid
    if (!(0, tokenService_1.validateToken)(accessToken)) {
        if (refreshToken && sessionId) {
            // If it's not, then try to refresh the tokens
            // if error is not caused by token refresh, throw the error.
            try {
                const token = await (0, tokenService_1.tokenExchange)(authPayload.authDomain, {
                    grantType: "refresh_token",
                    refreshToken,
                    scope: authPayload.scope,
                    clientId: authPayload.clientId,
                    deviceId: authPayload.deviceId,
                    sessionId,
                    anonymousId
                });
                (0, storage_1.setItem)("token", token.accessToken, { domain: cookieDomain, expires: (0, tokenService_1.getAccessTokenExpirationDate)(token.expiresIn) });
                (0, storage_1.setItem)("refreshToken", token.refreshToken, { domain: cookieDomain, expires: (0, tokenService_1.getRefreshAndSessionExpirationDate)(token.expiresIn) });
                (0, storage_1.setItem)("sessionId", sessionId, { domain: cookieDomain, expires: (0, tokenService_1.getRefreshAndSessionExpirationDate)(token.expiresIn) });
            }
            catch (e) {
                if (e instanceof Error && e.message !== constant_1.ERROR_MESSAGE.TOKEN_REFRESH_FAIL) {
                    throw e;
                }
            }
        }
    }
    const redirectToCommonAuth = (flowEntry, autoFillParams, templateId, replaceUrl = false) => {
        // generate a state and store it in cookie
        const state = (0, uuid_1.v4)();
        (0, storage_1.setItem)("state", state, { domain: cookieDomain });
        const { email, firstName, lastName } = autoFillParams || {};
        const clientDeviceModel = authPayload.clientDeviceModel;
        // initialize query params
        const queryParams = {
            clientId: authPayload.clientId,
            redirectUri: authPayload.redirectUri,
            deviceId: authPayload.deviceId,
            flowEntry: flowEntry,
            scope: authPayload.scope,
            responseType: "code",
            state,
            ...firstName && { firstName },
            ...lastName && { lastName },
            ...email && { email },
            ...templateId && { t: templateId },
            ...clientDeviceModel && { clientDeviceModel },
        };
        // generate the url
        const queryString = new URLSearchParams(queryParams).toString();
        const url = `${authPayload.authDomain}/authorize?${queryString}`;
        // redirect to the url
        if (replaceUrl) {
            location.replace(url);
            return;
        }
        else {
            location.assign(url);
            return;
        }
    };
    const signInWithRedirect = (email, templateId, replaceUrl = false) => {
        // if there is a refresh token or access token in cookie,
        // redirect to the redirect uri
        const refreshToken = (0, storage_1.getItem)("refreshToken");
        const accessToken = (0, storage_1.getItem)("token");
        if (refreshToken || accessToken) {
            location.assign(authPayload.redirectUri);
            return;
        }
        redirectToCommonAuth("signin", { email }, templateId, replaceUrl);
    };
    const mplSignInWithRedirect = (email) => {
        redirectToCommonAuth("mplsignin", { email });
    };
    const signUpWithRedirect = (autoFillParams, templateId, replaceUrl = false) => {
        redirectToCommonAuth("signup", autoFillParams, templateId, replaceUrl);
    };
    const handleRedirectCallback = async () => {
        // if there is an access token in cookie, return
        const accessToken = (0, storage_1.getItem)("token");
        if (accessToken) {
            return;
        }
        // if there is a refresh token in cookie, get access token
        const refreshToken = (0, storage_1.getItem)("refreshToken");
        if (refreshToken) {
            try {
                await refreshAccessToken();
                return;
            }
            catch {
                // if refresh fails, remove refresh token and call signInWithRedirect
                (0, storage_1.removeItem)("refreshToken", { domain: cookieDomain });
                signInWithRedirect();
            }
        }
        // get the code,state and sessionId from the url
        const url = new URL(location.href);
        const code = url.searchParams.get("code");
        const state = url.searchParams.get("state");
        const sessionId = url.searchParams.get("sessionId");
        const error = url.searchParams.get("error");
        // handle error codes
        if (error === constant_1.ErrorCode.E0013) {
            throw new Error(constant_1.ERROR_MESSAGE.AUTH_SESSION_EXPIRED);
        }
        // validate the code, state and sessionId
        if (!code || !state || !sessionId) {
            throw new Error(constant_1.ERROR_MESSAGE.INVALID_CALLBACK_URL);
        }
        // validate the callback state with the state stored in the cookie
        const storedState = (0, storage_1.getItem)("state");
        if (!storedState || storedState !== state) {
            throw new Error(constant_1.ERROR_MESSAGE.INVALID_CALLBACK_URL);
        }
        // initialize the query params for token exchange
        const queryParams = {
            clientId: authPayload.clientId,
            code,
            grantType: "authorization_code",
            redirectUri: authPayload.redirectUri,
            deviceId: authPayload.deviceId,
            clientSecret: authPayload.clientSecret,
            sessionId: sessionId,
            anonymousId
        };
        const token = await (0, tokenService_1.tokenExchange)(authPayload.authDomain, queryParams);
        // set access token, refresh token and session id in cookie
        (0, storage_1.setItem)("token", token.accessToken, { domain: cookieDomain, expires: (0, tokenService_1.getAccessTokenExpirationDate)(token.expiresIn) });
        (0, storage_1.setItem)("refreshToken", token.refreshToken, { domain: cookieDomain, expires: (0, tokenService_1.getRefreshAndSessionExpirationDate)(token.expiresIn) });
        (0, storage_1.setItem)("sessionId", sessionId, { domain: cookieDomain, expires: (0, tokenService_1.getRefreshAndSessionExpirationDate)(token.expiresIn) });
    };
    const signOut = async () => {
        const refreshToken = (0, storage_1.getItem)("refreshToken");
        if (refreshToken) {
            // call auth server to sign out
            const queryParams = {
                clientId: authPayload.clientId,
                deviceId: authPayload.deviceId,
                refreshToken: refreshToken,
            };
            try {
                await (0, signOutService_1.signOut)(authPayload.authDomain, queryParams);
            }
            catch (e) {
                // if sign out fails, carry on, do not throw error as access token is already removed from memory
                // TODO: how to update auth api that the this session is signed out
            }
        }
    };
    const refreshAccessToken = async () => {
        // get refresh token and session id from cookie
        const refreshToken = (0, storage_1.getItem)("refreshToken");
        const sessionId = (0, storage_1.getItem)("sessionId");
        // initialize the query params for token exchange
        const queryParams = {
            clientId: authPayload.clientId,
            deviceId: authPayload.deviceId,
            sessionId,
            grantType: "refresh_token",
            refreshToken,
            scope: authPayload.scope,
            anonymousId
        };
        const token = await (0, tokenService_1.tokenExchange)(authPayload.authDomain, queryParams);
        // set access and refresh token in cookie
        (0, storage_1.setItem)("token", token.accessToken, { domain: cookieDomain, expires: (0, tokenService_1.getAccessTokenExpirationDate)(token.expiresIn) });
        (0, storage_1.setItem)("refreshToken", token.refreshToken, { domain: cookieDomain, expires: (0, tokenService_1.getRefreshAndSessionExpirationDate)(token.expiresIn) });
        if (sessionId) {
            (0, storage_1.setItem)("sessionId", sessionId, { domain: cookieDomain, expires: (0, tokenService_1.getRefreshAndSessionExpirationDate)(token.expiresIn) });
        }
    };
    const isAuthenticated = () => {
        return (0, tokenService_1.validateToken)((0, storage_1.getItem)("token"));
    };
    const getUser = () => {
        try {
            const accessToken = (0, storage_1.getItem)("token");
            if (accessToken) {
                const decodedToken = (0, jwt_decode_1.default)(accessToken);
                return decodedToken.sub;
            }
        }
        catch {
            return undefined;
        }
        return undefined;
    };
    const getAccessToken = () => {
        return (0, storage_1.getItem)("token");
    };
    const removeTokensAndSessionId = () => {
        (0, storage_1.removeItem)("token", { domain: cookieDomain });
        (0, storage_1.removeItem)("refreshToken", { domain: cookieDomain });
        (0, storage_1.removeItem)("sessionId", { domain: cookieDomain });
    };
    return {
        signInWithRedirect,
        mplSignInWithRedirect,
        signUpWithRedirect,
        handleRedirectCallback,
        signOut,
        isAuthenticated,
        getUser,
        getAccessToken,
        removeTokensAndSessionId,
        refreshAccessToken,
    };
};
exports.init = init;
